import type { AuthScopeToken } from "@/lib/auth/authScopeGeneration";
import { isCurrentAuthScope } from "@/lib/auth/authScopeGeneration";
import type {
  ConnectorOAuthAttempt,
  ConnectorOAuthAttemptEntry,
  ConnectorOAuthLocalIntent,
} from "@/models/connectorOAuth";
import { isConnectorOAuthAttemptTerminal } from "@/models/connectorOAuth";
import {
  cancelConnectorOAuthAttempt,
  fetchActiveConnectorOAuthAttempts,
  fetchConnectorOAuthAttempt,
} from "@/services/connectorOAuthService";
import {
  CONNECTOR_OAUTH_BROADCAST_CHANNEL,
  CONNECTOR_OAUTH_WAKE_TYPE,
  createConnectorOAuthAck,
  isConnectorOAuthProtocolMessage,
  type ConnectorOAuthWakeMessage,
} from "@/lib/oauth/connectorOAuthProtocol";
import { ConnectorOAuthIntentIndex } from "@/lib/oauth/connectorOAuthIntentIndex";
import type { ConnectorOAuthAdapter } from "@/lib/oauth/sourceCredentialOAuthAdapter";
import {
  getConnectorOAuthEntry,
  useConnectorOAuthStore,
} from "@/stores/useConnectorOAuthStore";
import { useSourceCredentialStore } from "@/stores/useSourceCredentialStore";
import { useSourceConnectionStore } from "@/stores/useSourceConnectionStore";
import { getProblemDetail } from "@/types/problem-detail";

const POPUP_FEATURES =
  "popup=yes,width=620,height=760,resizable=yes,scrollbars=yes";
const SAFETY_CONFIRM_MS = 5 * 60 * 1000;

export interface ConnectorOAuthPopupReservation {
  readonly reservationId: string;
  readonly popup: Window;
}

export interface StartConnectorOAuthOptions<TTarget> {
  adapter: ConnectorOAuthAdapter<TTarget>;
  target: TTarget;
  reservation?: ConnectorOAuthPopupReservation;
  signal?: AbortSignal;
}

export class ConnectorOAuthPopupBlockedError extends Error {
  readonly code = "POPUP_BLOCKED";

  constructor() {
    super("The browser blocked the secure provider window. Allow popups and try again.");
    this.name = "ConnectorOAuthPopupBlockedError";
  }
}

/** Thrown when popup navigation fails after the backend created a durable attempt. */
export class ConnectorOAuthNavigationError extends Error {
  readonly code = "OAUTH_NAVIGATION_FAILED";

  constructor(readonly attemptPublicId: string) {
    super("Could not open the provider window. Use Continue to try again.");
    this.name = "ConnectorOAuthNavigationError";
  }
}

function createReservationId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function closePopupQuietly(popup: Window | undefined): void {
  if (!popup) return;
  try {
    popup.close();
  } catch {
    // A provider COOP response may detach the Window proxy.
  }
}

function enrichAttempt(
  attempt: ConnectorOAuthAttempt,
  intent: ConnectorOAuthLocalIntent | null,
  previous?: ConnectorOAuthAttempt,
): ConnectorOAuthAttempt {
  return {
    ...attempt,
    provider: attempt.provider ?? previous?.provider ?? null,
    workspacePublicId:
      attempt.workspacePublicId ??
      intent?.workspacePublicId ??
      previous?.workspacePublicId ??
      null,
    surface: attempt.surface ?? intent?.surface ?? previous?.surface ?? null,
    returnPath:
      attempt.returnPath ?? intent?.returnPath ?? previous?.returnPath ?? null,
    projectPublicId:
      attempt.projectPublicId ??
      intent?.projectPublicId ??
      previous?.projectPublicId ??
      null,
    conversationPublicId:
      attempt.conversationPublicId ??
      intent?.conversationPublicId ??
      previous?.conversationPublicId ??
      null,
    toolCallId:
      attempt.toolCallId ?? intent?.toolCallId ?? previous?.toolCallId ?? null,
    connectionPublicId:
      attempt.connectionPublicId ??
      intent?.connectionPublicId ??
      previous?.connectionPublicId ??
      null,
    pluginKey:
      attempt.pluginKey ?? intent?.pluginKey ?? previous?.pluginKey ?? null,
    connectionOptionKey:
      attempt.connectionOptionKey ??
      intent?.connectionOptionKey ??
      previous?.connectionOptionKey ??
      null,
    connectionIntent:
      attempt.connectionIntent ??
      intent?.connectionIntent ??
      previous?.connectionIntent ??
      null,
  };
}

/**
 * Auth-scoped owner for connector OAuth browser state and backend confirmations.
 *
 * Popup callbacks only wake this coordinator. Every state transition is confirmed through the
 * attempt service, with one in-flight GET allowed per attempt.
 */
export class ConnectorOAuthCoordinator {
  private authScope: AuthScopeToken | null = null;
  private listening = false;
  private broadcast: BroadcastChannel | null = null;
  private readonly reservations = new Map<string, Window>();
  private readonly popupsByAttemptId = new Map<string, Window>();
  private readonly popupAbandonWatchers = new Map<string, () => void>();
  private readonly confirmInflight = new Map<
    string,
    Promise<ConnectorOAuthAttempt>
  >();
  private readonly safetyTimers = new Map<string, number>();
  private readonly safetyConfirmedAttemptIds = new Set<string>();
  private readonly authScopedWaiterRejectors = new Set<(reason: Error) => void>();
  private readonly attemptWaiterRejectors = new Map<
    string,
    Set<(reason: Error) => void>
  >();
  private readonly startedAttemptIds = new Set<string>();
  private readonly departedAttemptIds = new Set<string>();
  private readonly intentIndex = new ConnectorOAuthIntentIndex();
  private visibleConversationId: string | null = null;
  private gapFillInflight: Promise<void> | null = null;

  private readonly onWindowMessage = (event: MessageEvent<unknown>) => {
    if (event.origin !== window.location.origin) return;
    if (!isConnectorOAuthProtocolMessage(event.data)) return;
    if (event.data.type !== CONNECTOR_OAUTH_WAKE_TYPE) return;

    const popup = this.popupsByAttemptId.get(event.data.attemptPublicId);
    if (!popup || event.source !== popup) return;
    this.acknowledgeWake(event.data, popup);
  };

  private readonly onBroadcastMessage = (event: MessageEvent<unknown>) => {
    if (!isConnectorOAuthProtocolMessage(event.data)) return;
    if (event.data.type !== CONNECTOR_OAUTH_WAKE_TYPE) return;
    const attemptPublicId = event.data.attemptPublicId;
    if (!getConnectorOAuthEntry(attemptPublicId) && !this.intentIndex.get(attemptPublicId)) {
      return;
    }
    this.acknowledgeWake(event.data);
  };

  /** Activates listeners and restores active attempts for an authenticated user. */
  activate(authScope: AuthScopeToken): void {
    if (
      this.authScope &&
      this.authScope.authScopeKey !== authScope.authScopeKey
    ) {
      this.deactivate();
    }
    this.authScope = authScope;
    this.ensureListeners();
    void this.runGapFill().catch(() => undefined);
  }

  /** Clears browser resources and OAuth/source caches at the auth boundary. */
  deactivate(): void {
    if (typeof window !== "undefined" && this.listening) {
      window.removeEventListener("message", this.onWindowMessage);
    }
    this.listening = false;
    this.broadcast?.removeEventListener("message", this.onBroadcastMessage);
    this.broadcast?.close();
    this.broadcast = null;
    for (const popup of this.reservations.values()) closePopupQuietly(popup);
    for (const popup of this.popupsByAttemptId.values()) closePopupQuietly(popup);
    for (const cleanup of this.popupAbandonWatchers.values()) cleanup();
    if (typeof window !== "undefined") {
      for (const timer of this.safetyTimers.values()) window.clearTimeout(timer);
    }
    this.reservations.clear();
    this.popupsByAttemptId.clear();
    this.popupAbandonWatchers.clear();
    this.confirmInflight.clear();
    this.safetyTimers.clear();
    this.safetyConfirmedAttemptIds.clear();
    this.intentIndex.clear();
    this.startedAttemptIds.clear();
    this.departedAttemptIds.clear();
    const scopeError = new DOMException("OAuth auth scope ended", "AbortError");
    for (const rejectWaiter of this.authScopedWaiterRejectors) {
      rejectWaiter(scopeError);
    }
    this.authScopedWaiterRejectors.clear();
    this.attemptWaiterRejectors.clear();
    this.visibleConversationId = null;
    this.gapFillInflight = null;
    this.authScope = null;
    useConnectorOAuthStore.getState().reset();
    useSourceCredentialStore.getState().reset();
    useSourceConnectionStore.getState().reset();
  }

  /**
   * Opens a same-origin placeholder synchronously from a user gesture.
   *
   * The reservation can be held while a connection attempt is prepared, then consumed by
   * {@link startAuthorization}.
   */
  reservePopup(): ConnectorOAuthPopupReservation {
    this.ensureListeners();
    const reservationId = createReservationId();
    const popup = window.open(
      "about:blank",
      `cns-connector-oauth-${reservationId}`,
      POPUP_FEATURES,
    );
    if (!popup) throw new ConnectorOAuthPopupBlockedError();

    try {
      popup.document.title = "Connecting plugin";
      popup.document.body.innerHTML =
        '<main style="font:14px system-ui;padding:32px;color:#334155">Preparing secure authorization…</main>';
    } catch {
      // about:blank is normally same-origin; presentation is non-essential.
    }
    this.reservations.set(reservationId, popup);
    return { reservationId, popup };
  }

  /** Closes an unused popup reservation. */
  releasePopup(reservation: ConnectorOAuthPopupReservation | undefined): void {
    if (!reservation) return;
    const popup = this.reservations.get(reservation.reservationId);
    this.reservations.delete(reservation.reservationId);
    closePopupQuietly(popup);
  }

  /**
   * Focuses a tracked live popup for an owned attempt without issuing HTTP.
   *
   * @returns True when a live popup was focused in this tab.
   */
  focusAttempt(attemptPublicId: string): boolean {
    const popup = this.popupsByAttemptId.get(attemptPublicId);
    if (!popup || popup.closed) return false;
    try {
      popup.focus();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Starts authorization with listeners already registered and navigates the reserved popup.
   *
   * @throws {DOMException} When the authorization-start fetch is aborted
   * @throws {ConnectorOAuthNavigationError} When popup navigation fails after attempt creation
   */
  async startAuthorization<TTarget>(
    options: StartConnectorOAuthOptions<TTarget>,
  ): Promise<ConnectorOAuthAttempt> {
    this.ensureListeners();
    const authScopeAtStart = this.authScope;
    if (!this.isScopeCurrent(authScopeAtStart)) {
      throw new DOMException("OAuth auth scope ended", "AbortError");
    }
    const reservation = options.reservation ?? this.reservePopup();
    const popup = this.reservations.get(reservation.reservationId);
    if (!popup || popup !== reservation.popup) {
      throw new Error("OAuth popup reservation is no longer available");
    }
    if (options.signal?.aborted) {
      this.releasePopup(reservation);
      throw new DOMException("OAuth authorization start aborted", "AbortError");
    }

    let authorization;
    try {
      authorization = await options.adapter.authorize(
        options.target,
        options.signal,
      );
    } catch (error) {
      this.releasePopup(reservation);
      throw error;
    }
    if (!this.isScopeCurrent(authScopeAtStart)) {
      this.releasePopup(reservation);
      throw new DOMException("OAuth auth scope changed", "AbortError");
    }

    this.reservations.delete(reservation.reservationId);
    const intent: ConnectorOAuthLocalIntent = {
      attemptPublicId: authorization.attemptPublicId,
      credentialPublicId: authorization.credentialPublicId,
      createdAt: Date.now(),
      ...authorization.context,
    };
    this.intentIndex.save(intent);
    this.startedAttemptIds.add(authorization.attemptPublicId);
    this.popupsByAttemptId.set(authorization.attemptPublicId, popup);

    const optimisticAttempt: ConnectorOAuthAttempt = {
      attemptPublicId: authorization.attemptPublicId,
      credentialPublicId: authorization.credentialPublicId,
      status: "pending",
      toolResultApplied: false,
      expiresAt: authorization.expiresAt,
      workspacePublicId: authorization.context.workspacePublicId,
      surface: authorization.context.surface,
      returnPath: authorization.context.returnPath,
      projectPublicId: authorization.context.projectPublicId ?? null,
      conversationPublicId: authorization.context.conversationPublicId ?? null,
      toolCallId: authorization.context.toolCallId ?? null,
      connectionPublicId: authorization.context.connectionPublicId ?? null,
      pluginKey: authorization.context.pluginKey ?? null,
      connectionOptionKey: authorization.context.connectionOptionKey ?? null,
      connectionIntent: authorization.context.connectionIntent ?? null,
    };
    this.upsertAttemptIfCurrent(authScopeAtStart, optimisticAttempt, {
      startedInThisTab: true,
    });
    this.scheduleSafetyConfirm(authorization.attemptPublicId);

    try {
      popup.location.replace(authorization.authorizationUrl);
    } catch {
      closePopupQuietly(popup);
      this.popupsByAttemptId.delete(authorization.attemptPublicId);
      throw new ConnectorOAuthNavigationError(authorization.attemptPublicId);
    }
    this.attachPopupAbandonWatcher(authorization.attemptPublicId, popup);
    return optimisticAttempt;
  }

  /** Reads one attempt with per-attempt single-flight request coalescing. */
  confirmAttempt(attemptPublicId: string): Promise<ConnectorOAuthAttempt> {
    const existing = this.confirmInflight.get(attemptPublicId);
    if (existing) return existing;

    const authScopeAtStart = this.authScope;
    const request = fetchConnectorOAuthAttempt(attemptPublicId)
      .then((attempt) =>
        this.isScopeCurrent(authScopeAtStart)
          ? this.applyBackendAttempt(attempt, authScopeAtStart)
          : attempt,
      )
      .catch((error: unknown) => {
        const status = getProblemDetail(error)?.status;
        if (status === 401 || status === 403 || status === 404) {
          this.rejectAttemptWaiters(
            attemptPublicId,
            error instanceof Error
              ? error
              : new Error("Plugin authorization can no longer be confirmed."),
          );
        }
        throw error;
      })
      .finally(() => {
        if (this.confirmInflight.get(attemptPublicId) === request) {
          this.confirmInflight.delete(attemptPublicId);
        }
      });
    this.confirmInflight.set(attemptPublicId, request);
    return request;
  }

  /**
   * Confirms an attempt after a mutating action, ignoring any stale in-flight read.
   */
  async confirmAttemptFresh(
    attemptPublicId: string,
  ): Promise<ConnectorOAuthAttempt> {
    const inflight = this.confirmInflight.get(attemptPublicId);
    if (inflight) {
      await inflight.catch(() => undefined);
      this.confirmInflight.delete(attemptPublicId);
    }
    return this.confirmAttempt(attemptPublicId);
  }

  /** Waits for a backend-confirmed terminal attempt without polling. */
  waitForTerminal(
    attemptPublicId: string,
    signal?: AbortSignal,
  ): Promise<ConnectorOAuthAttempt> {
    const current = getConnectorOAuthEntry(attemptPublicId)?.attempt;
    if (current && isConnectorOAuthAttemptTerminal(current.status)) {
      return Promise.resolve(current);
    }
    if (signal?.aborted) {
      return Promise.reject(
        new DOMException("OAuth attempt wait aborted", "AbortError"),
      );
    }

    return new Promise<ConnectorOAuthAttempt>((resolve, reject) => {
      const cleanup = () => {
        unsubscribe();
        signal?.removeEventListener("abort", onAbort);
        this.authScopedWaiterRejectors.delete(onAuthScopeEnded);
        const rejectors = this.attemptWaiterRejectors.get(attemptPublicId);
        rejectors?.delete(onAttemptRejected);
        if (rejectors?.size === 0) {
          this.attemptWaiterRejectors.delete(attemptPublicId);
        }
      };
      const onAbort = () => {
        cleanup();
        reject(new DOMException("OAuth attempt wait aborted", "AbortError"));
      };
      const onAuthScopeEnded = (reason: Error) => {
        cleanup();
        reject(reason);
      };
      const onAttemptRejected = (reason: Error) => {
        cleanup();
        reject(reason);
      };
      const unsubscribe = useConnectorOAuthStore.subscribe((state) => {
        const attempt = state.entriesByAttemptId[attemptPublicId]?.attempt;
        if (!attempt || !isConnectorOAuthAttemptTerminal(attempt.status)) return;
        cleanup();
        resolve(attempt);
      });
      signal?.addEventListener("abort", onAbort, { once: true });
      this.authScopedWaiterRejectors.add(onAuthScopeEnded);
      const rejectors =
        this.attemptWaiterRejectors.get(attemptPublicId) ?? new Set();
      rejectors.add(onAttemptRejected);
      this.attemptWaiterRejectors.set(attemptPublicId, rejectors);
    });
  }

  /** Explicitly cancels an attempt through the backend. */
  async cancelAttempt(attemptPublicId: string): Promise<ConnectorOAuthAttempt> {
    const authScopeAtStart = this.authScope;
    const attempt = await cancelConnectorOAuthAttempt(attemptPublicId);
    return this.isScopeCurrent(authScopeAtStart)
      ? this.applyBackendAttempt(attempt, authScopeAtStart)
      : attempt;
  }

  /**
   * Runs one connect-driven active-attempt and loaded-source cache gap-fill.
   *
   * Concurrent reconnect signals share the same request.
   */
  runGapFill(): Promise<void> {
    if (!this.authScope) return Promise.resolve();
    if (this.gapFillInflight) return this.gapFillInflight;
    const authScopeAtStart = this.authScope;

    const request = Promise.all([
      fetchActiveConnectorOAuthAttempts().then((attempts) => {
        if (!this.isScopeCurrent(authScopeAtStart)) return;
        for (const attempt of attempts) {
          this.applyBackendAttempt(attempt, authScopeAtStart);
        }
      }),
      useSourceCredentialStore.getState().refreshLoadedWorkspaces(),
      useSourceConnectionStore.getState().refreshLoadedProjects(),
    ])
      .then(() => undefined)
      .finally(() => {
        if (this.gapFillInflight === request) {
          this.gapFillInflight = null;
        }
      });
    this.gapFillInflight = request;
    return request;
  }

  /** Records the currently mounted chat surface for safe same-view auto-continuation. */
  setVisibleConversation(conversationPublicId: string): void {
    this.visibleConversationId = conversationPublicId;
  }

  /** Currently mounted chat conversation public id, if any. */
  getVisibleConversation(): string | null {
    return this.visibleConversationId;
  }

  /** Marks in-memory attempts as navigated away so returning never auto-continues them. */
  clearVisibleConversation(conversationPublicId: string): void {
    if (this.visibleConversationId === conversationPublicId) {
      this.visibleConversationId = null;
    }
    for (const [attemptPublicId, entry] of Object.entries(
      useConnectorOAuthStore.getState().entriesByAttemptId,
    )) {
      if (
        entry.startedInThisTab &&
        entry.attempt.surface === "chat_connect_source" &&
        entry.attempt.conversationPublicId === conversationPublicId &&
        !entry.attempt.continuationClaimedAt
      ) {
        this.departedAttemptIds.add(attemptPublicId);
        if (entry.autoContinuationState === "eligible") {
          useConnectorOAuthStore
            .getState()
            .setAutoContinuationState(attemptPublicId, "suppressed");
        }
      }
    }
  }

  /** Atomically claims a local auto-continuation tab slot (not the server /chat claim). */
  claimAutoContinuationSlot(
    attemptPublicId: string,
    mode: "auto" | "explicit",
  ): boolean {
    const entry = getConnectorOAuthEntry(attemptPublicId);
    if (
      !entry ||
      !isConnectorOAuthAttemptTerminal(entry.attempt.status) ||
      !entry.attempt.toolResultApplied ||
      entry.attempt.continuationClaimedAt ||
      entry.autoContinuationState === "started"
    ) {
      return false;
    }
    if (mode === "auto") {
      if (
        this.departedAttemptIds.has(attemptPublicId) ||
        this.visibleConversationId !== entry.attempt.conversationPublicId ||
        document.visibilityState !== "visible"
      ) {
        if (entry.autoContinuationState === "eligible") {
          useConnectorOAuthStore
            .getState()
            .setAutoContinuationState(attemptPublicId, "suppressed");
        }
        return false;
      }
      if (
        entry.autoContinuationState !== "eligible" &&
        entry.autoContinuationState !== "suppressed"
      ) {
        return false;
      }
    }
    useConnectorOAuthStore
      .getState()
      .setAutoContinuationState(attemptPublicId, "started");
    return true;
  }

  /** Releases a local auto-continuation tab slot when chat never reached the backend claim. */
  releaseAutoContinuationSlot(attemptPublicId: string): void {
    const entry = getConnectorOAuthEntry(attemptPublicId);
    if (!entry?.attempt.continuationClaimedAt) {
      useConnectorOAuthStore
        .getState()
        .setAutoContinuationState(attemptPublicId, "none");
    }
  }

  private isScopeCurrent(scope: AuthScopeToken | null): boolean {
    return isCurrentAuthScope(scope) && scope === this.authScope;
  }

  private upsertAttemptIfCurrent(
    scope: AuthScopeToken | null,
    attempt: ConnectorOAuthAttempt,
    meta: {
      startedInThisTab?: boolean;
      lastConfirmedAt?: number;
      autoContinuationState?: ConnectorOAuthAttemptEntry["autoContinuationState"];
    },
  ): void {
    if (!this.isScopeCurrent(scope)) return;
    useConnectorOAuthStore.getState().upsertAttempt(attempt, meta);
  }

  private ensureListeners(): void {
    if (typeof window === "undefined" || this.listening) return;
    window.addEventListener("message", this.onWindowMessage);
    try {
      this.broadcast = new BroadcastChannel(CONNECTOR_OAUTH_BROADCAST_CHANNEL);
      this.broadcast.addEventListener("message", this.onBroadcastMessage);
    } catch {
      this.broadcast = null;
    }
    this.listening = true;
  }

  private acknowledgeWake(
    wake: ConnectorOAuthWakeMessage,
    directTarget?: Window,
  ): void {
    const ack = createConnectorOAuthAck(wake);
    if (directTarget) {
      try {
        directTarget.postMessage(ack, window.location.origin);
      } catch {
        // Broadcast ACK remains available after COOP detachment.
      }
    }
    try {
      this.broadcast?.postMessage(ack);
    } catch {
      // ACK is advisory; the callback still renders backend-confirmed fallback state.
    }
    void this.confirmAttempt(wake.attemptPublicId).catch(() => undefined);
  }

  private rejectAttemptWaiters(attemptPublicId: string, reason: Error): void {
    const rejectors = this.attemptWaiterRejectors.get(attemptPublicId);
    if (!rejectors) return;
    for (const rejectWaiter of [...rejectors]) rejectWaiter(reason);
  }

  /**
   * When the user closes the provider popup without finishing OAuth, the parent window
   * regains focus. That focus event is the abandonment signal — no polling loop.
   */
  private attachPopupAbandonWatcher(attemptPublicId: string, popup: Window): void {
    this.detachPopupAbandonWatcher(attemptPublicId);
    const onFocus = () => {
      if (!popup.closed) return;
      void this.handlePopupClosedByUser(attemptPublicId);
    };
    window.addEventListener("focus", onFocus);
    this.popupAbandonWatchers.set(attemptPublicId, () => {
      window.removeEventListener("focus", onFocus);
    });
  }

  private detachPopupAbandonWatcher(attemptPublicId: string): void {
    this.popupAbandonWatchers.get(attemptPublicId)?.();
    this.popupAbandonWatchers.delete(attemptPublicId);
  }

  private async handlePopupClosedByUser(attemptPublicId: string): Promise<void> {
    const entry = getConnectorOAuthEntry(attemptPublicId);
    if (!entry || isConnectorOAuthAttemptTerminal(entry.attempt.status)) {
      this.detachPopupAbandonWatcher(attemptPublicId);
      return;
    }
    this.detachPopupAbandonWatcher(attemptPublicId);
    try {
      await this.cancelAttempt(attemptPublicId);
    } catch {
      void this.confirmAttempt(attemptPublicId).catch(() => undefined);
    }
  }

  private applyBackendAttempt(
    backendAttempt: ConnectorOAuthAttempt,
    scope: AuthScopeToken | null = this.authScope,
  ): ConnectorOAuthAttempt {
    if (!this.isScopeCurrent(scope)) return backendAttempt;

    const current = getConnectorOAuthEntry(backendAttempt.attemptPublicId);
    const intent = this.intentIndex.get(backendAttempt.attemptPublicId);
    const attempt = enrichAttempt(backendAttempt, intent, current?.attempt);
    let autoContinuationState = current?.autoContinuationState ?? "none";

    if (
      isConnectorOAuthAttemptTerminal(attempt.status) &&
      attempt.surface === "chat_connect_source" &&
      attempt.toolResultApplied &&
      !attempt.continuationClaimedAt &&
      autoContinuationState === "none"
    ) {
      const canAutoContinue =
        this.startedAttemptIds.has(attempt.attemptPublicId) &&
        !this.departedAttemptIds.has(attempt.attemptPublicId) &&
        this.visibleConversationId === attempt.conversationPublicId &&
        document.visibilityState === "visible";
      autoContinuationState = canAutoContinue ? "eligible" : "suppressed";
    }

    useConnectorOAuthStore.getState().upsertAttempt(attempt, {
      startedInThisTab:
        current?.startedInThisTab ??
        this.startedAttemptIds.has(attempt.attemptPublicId),
      lastConfirmedAt: Date.now(),
      autoContinuationState,
    });

    if (isConnectorOAuthAttemptTerminal(attempt.status)) {
      const timer = this.safetyTimers.get(attempt.attemptPublicId);
      if (timer !== undefined) window.clearTimeout(timer);
      this.safetyTimers.delete(attempt.attemptPublicId);
      this.detachPopupAbandonWatcher(attempt.attemptPublicId);
      closePopupQuietly(this.popupsByAttemptId.get(attempt.attemptPublicId));
      this.popupsByAttemptId.delete(attempt.attemptPublicId);
      this.refreshCorrelatedCaches(attempt);
    } else {
      this.scheduleSafetyConfirm(attempt.attemptPublicId);
    }
    return attempt;
  }

  private scheduleSafetyConfirm(attemptPublicId: string): void {
    if (
      this.safetyTimers.has(attemptPublicId) ||
      this.safetyConfirmedAttemptIds.has(attemptPublicId)
    ) {
      return;
    }
    const timer = window.setTimeout(() => {
      this.safetyTimers.delete(attemptPublicId);
      this.safetyConfirmedAttemptIds.add(attemptPublicId);
      void this.confirmAttempt(attemptPublicId).catch(() => undefined);
    }, SAFETY_CONFIRM_MS);
    this.safetyTimers.set(attemptPublicId, timer);
  }

  private refreshCorrelatedCaches(attempt: ConnectorOAuthAttempt): void {
    const credentialStatus =
      attempt.status === "succeeded"
        ? "connected"
        : null;
    useSourceCredentialStore.getState().applyCredentialPatch({
      credentialPublicId: attempt.credentialPublicId,
      workspacePublicId: attempt.workspacePublicId,
      status: credentialStatus,
      errorCode: attempt.status === "succeeded" ? null : undefined,
    });
    useSourceConnectionStore.getState().applyConnectionPatch({
      connectionPublicId: attempt.connectionPublicId,
      credentialPublicId: attempt.credentialPublicId,
      workspacePublicId: attempt.workspacePublicId,
      projectPublicId: attempt.projectPublicId,
      credentialStatus,
      effectiveStatus:
        attempt.status === "succeeded"
          ? "connected"
          : null,
    });
    void useSourceCredentialStore.getState().refreshLoadedWorkspaces();
    void useSourceConnectionStore.getState().refreshLoadedProjects({
      projectPublicId: attempt.projectPublicId,
      workspacePublicId: attempt.workspacePublicId,
    });
  }
}

export const connectorOAuthCoordinator = new ConnectorOAuthCoordinator();
